import { supabase } from './supabase';
import { useUserStore } from '../store/userStore';
import { VERSION_CONSENTEMENT } from '../config/terms';
import type { Plan, Profile } from '../types';
import type { SubscriptionStatus } from '../utils/subscriptionExpiry';

// Email de connexion (auth) — sert à préremplir l'email de contact si vide.
export function getAuthEmail(): string | null {
  return useUserStore.getState().user?.email ?? null;
}

export async function getProfile(): Promise<Profile | null> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) throw new Error('Tu dois être connecté.');

  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
  if (error) throw error;
  return (data as Profile | null) ?? null;
}

export async function updateProfile(patch: Partial<Profile>): Promise<void> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) throw new Error('Tu dois être connecté.');

  // full_name dérivé pour rester cohérent avec les lecteurs existants (greeting, etc.).
  const fullName = [patch.first_name, patch.last_name]
    .map((v) => v?.trim())
    .filter(Boolean)
    .join(' ');
  const payload = { ...patch, ...(fullName ? { full_name: fullName } : {}) };

  const { error } = await supabase.from('profiles').update(payload).eq('id', userId);
  if (error) throw error;
}

// Statut admin de l'utilisateur courant : présence d'une ligne dans admin_users (lecture-own via RLS).
// L'écriture est impossible côté client (aucune policy) → un utilisateur ne peut pas se promouvoir.
export async function isCurrentUserAdmin(): Promise<boolean> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) return false;

  const { data } = await supabase
    .from('admin_users')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();
  return !!data;
}

// Enregistre l'acceptation des CGU + politique de confidentialité (preuve de consentement).
export async function acceptTerms(): Promise<void> {
  await updateProfile({ terms_accepted_at: new Date().toISOString(), terms_version: VERSION_CONSENTEMENT });
}

// 'never' = aucun consentement enregistré (inscription OAuth) ; 'outdated' = consentement donné à
// une version antérieure du bundle → re-sollicitation (§12 : toute modification substantielle est
// re-soumise). Pilote l'étape post-auth obligatoire.
export type TermsStatus = 'accepted' | 'never' | 'outdated';

export async function getTermsStatus(): Promise<TermsStatus> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) return 'accepted'; // pas d'utilisateur → ne bloque pas le flux

  const { data } = await supabase
    .from('profiles')
    .select('terms_accepted_at, terms_version')
    .eq('id', userId)
    .maybeSingle();
  const row = data as { terms_accepted_at?: string | null; terms_version?: string | null } | null;

  if (!row?.terms_accepted_at) return 'never';
  return row.terms_version === VERSION_CONSENTEMENT ? 'accepted' : 'outdated';
}

// L'utilisateur courant a-t-il déjà renseigné sa date de naissance ? Pilote l'étape post-auth.
export async function hasBirthDate(): Promise<boolean> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) return true; // pas d'utilisateur → on ne bloque pas le flux

  const { data } = await supabase
    .from('profiles')
    .select('birth_date')
    .eq('id', userId)
    .maybeSingle();
  return !!(data as { birth_date?: string | null } | null)?.birth_date;
}

// Plan courant : celui du SERVEUR (table subscriptions, alimentée par le webhook RevenueCat), le même
// que celui du gating (edge getPlan). On ne lit PAS l'entitlement RevenueCat local : il passe avant le
// webhook, donc afficherait « Pro » pendant que le serveur répond « free » à chaque appel IA. L'écran
// de paiement attend la confirmation serveur (utils/confirmerPlan) avant d'annoncer l'activation.
export async function getCurrentPlan(): Promise<Plan> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) return 'free';

  const { data } = await supabase
    .from('subscriptions')
    .select('plan')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing'])
    // Même filtre que le gating serveur (_shared/subscriptions.ts) : une échéance PASSÉE n'ouvre
    // plus rien. Sans lui, l'app afficherait « Pro » pendant que le serveur refuse chaque appel.
    // NULL reste passant (lignes free de l'inscription, licences école sans échéance).
    .or('current_period_end.is.null,current_period_end.gt.now()')
    .order('current_period_end', { ascending: false })
    .limit(1)
    .maybeSingle();
  return ((data?.plan as Plan) ?? 'free') as Plan;
}

// Abonnement courant complet (plan + statut + échéance). Utilisé par le rappel d'expiration.
// `current_period_end` porte une échéance dès qu'il y en a une : la bêta « pro pour tous » en pose
// une (migrations 0024 puis 0036), donc le rappel s'affiche pour ces comptes. Il ne dort que sur les
// lignes sans échéance — la ligne 'free' de l'inscription, une licence 'school'.
export async function getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
  const userId = useUserStore.getState().user?.id;
  if (!userId) return null;

  const { data } = await supabase
    .from('subscriptions')
    .select('plan, status, current_period_end, revenuecat_id')
    .eq('user_id', userId)
    .in('status', ['active', 'trialing'])
    .order('current_period_end', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!data) return null;

  return {
    plan: (data.plan as Plan) ?? 'free',
    status: data.status as string,
    currentPeriodEnd: (data.current_period_end as string | null) ?? null,
    // L'identifiant RevenueCat ne sort JAMAIS du service : on n'en garde que l'origine qu'il révèle.
    source: data.revenuecat_id ? 'revenuecat' : 'granted',
  };
}
